import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Input, Row } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { QrReader, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, storage } from '../utils';

// A machine spindle label QR is "<repair type>,<setup type>,<machine #>,<side>,<spindle>" (MachineLabelDto.CodeValue).
// The server reads the machine type from field 1 (0 on a repair-only label), so field 1 must be a whole number.
const SPINDLE_FIELD_COUNT = 5;
const WHOLE_NUMBER = /^\d+$/;
const NOT_A_SPINDLE_LABEL = "That isn't a machine spindle label. Scan the label on the machine.";
const UNREADABLE_SPINDLE_LABEL = "That spindle label couldn't be read. Scan it again or use Manual Entry.";

export default function MobileRwoScan(props) {
   const navigate = useNavigate();
   const { id } = useParams();
   const [data, setData] = useState("");
   const [openQr, setOpenQr] = useState(false);
   const [spin, setSpinner] = useState(false);
   const scanPending = useRef(false);
   const sleep = ms => new Promise(r => setTimeout(r, ms));

   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const hasScanner = savedDevice.hasScanner;

   useEffect(() => {
      if (hasScanner) inputFocus("searchInput");
      //setData("34,10,1,A,29");   // Testing TC2 #1A-29
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id])

   useEffect(() => {
      if (data && data.length > 0) {
         if (scanPending.current) return; // a second scan must not fire a second lookup or navigation
         // a scan was completed
         const values = data.split(",");
         if (values.length !== SPINDLE_FIELD_COUNT || !WHOLE_NUMBER.test(values[1].trim())) {
            toast.error(values.length !== SPINDLE_FIELD_COUNT ? NOT_A_SPINDLE_LABEL : UNREADABLE_SPINDLE_LABEL);
            setData("");
            if (hasScanner) inputFocus("searchInput");
            return;
         }
         let payload = {
            RwoMachineId: values[0],
            MachineId: Number(values[1].trim()),
            MachineNum: values[2],
            Side: values[3].startsWith("N") ? "N/A" : values[3],
            Spindle: values[4],
         }
         //console.log(payload);
         scanPending.current = true;
         setSpinner(true);
         api
            .post(`Repair/DoesRwoExist/`, payload)
            .then(response => {
               if (response.data.success) {
                  if(response.data.rwoId === 0) 
                     scanOrder(payload);
                  else
                     editOrder(response.data.rwoId);
               }
               else {
                  toast.error(response.data.message || UNREADABLE_SPINDLE_LABEL);
                  scanPending.current = false;
                  setData("");
                  setSpinner(false);
                  if (hasScanner) inputFocus("searchInput");
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error?.response?.data?.message || UNREADABLE_SPINDLE_LABEL);
               scanPending.current = false;
               setData("");
               setSpinner(false);
               if (hasScanner) inputFocus("searchInput");
            })
      }
      else if (hasScanner) inputFocus("searchInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data])

   function editOrder(rwoId) {
      var nav = constants.ENDPOINTS.mobileRwoEdit + "/" + rwoId;
      navigate(nav);
   }

   function manualOrder() {
      var nav = constants.ENDPOINTS.mobileRwoNew + "/" + id;   // pass machine type when provided
      navigate(nav);
   }

   function scanOrder(model) {
      var nav = constants.ENDPOINTS.mobileRwoCreate;
      navigate(nav, { state: { model } });
   }

   const handleFocus = (event) => event.target.select();

   function handleQrScan(val) {
      setOpenQr(false);  // Hide QR Scanner
      setData(val);
   }

   function handleQrScanFailure(val) {
      setOpenQr(false);  // Hide QR Scanner
      toast.warning("This device does not have a camera.");
   }

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   return (
      <DesktopContainer screenName="Create Repair Work Order">
         <ServiceThreadSpinner on={spin}>
            <div className="filter-header">
               <Row className="justify-content-center">
                  <Col xs="11">
                     <Button
                        title="newRwo"
                        className="shredButton w-100 btn-md my-3"
                        onClick={() => { manualOrder(); }}>
                        Manual Entry
                     </Button>
                  </Col>
               </Row>
               {!hasScanner ? ( // NOT a handheld device
                  <Row className="justify-content-center">
                     <Col xs={11}>
                        <Button className="shredButton w-100 btn-md my-3" onClick={() => setOpenQr(!openQr)}>
                           {openQr ? "Hide Scanner" : "Scan QR Code"}
                        </Button>
                     </Col>
                  </Row>
               ) : null }
               <Row className="justify-content-center">
                  <Col xs="11">
                     <Button
                        title="To Search"
                        className="btn btn-md my-3 w-100"
                        onClick={() => navigate(`/mobile-rwo-search`)}>
                        BACK TO SEARCH
                     </Button>
                  </Col>
               </Row>
               {hasScanner ? ( // Handheld device
                  <div>
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="justify-content-center">
                        <Col xs="11">
                           <Input
                              name="searchInput"
                              placeholder="Focus here to scan QR code"
                              className="h-100"
                              onFocus={handleFocus}
                              onChange={(e) => handleQrScan(e.target.value)}
                           />
                        </Col>
                     </Row>
                  </div>
               ) : null }
               
               {openQr && <QrReader callback={handleQrScan} callbackError={handleQrScanFailure} />}
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer >
   );
}
